import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import Header from "@/components/site/Header";
import TopMarquee from "@/components/site/TopMarquee";
import Footer from "@/components/site/Footer";
import { getPolicy, POLICY_ORDER, POLICIES } from "@/data/policies";
import { useLang } from "@/context/LangContext";

export default function PolicyPage() {
  const { slug } = useParams();
  const { t, isEn } = useLang();
  const p = getPolicy(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (p) document.title = `${p.eyebrow} | ${isEn ? "Denva Post" : "देनवा पोस्ट"}`;
  }, [slug, isEn, p]);

  if (!p) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex flex-col items-center justify-center font-sans-dev px-4 text-center">
        <div className="font-serif-dev text-5xl font-black mb-4">404</div>
        <p className="text-[#4A4A4A] mb-6">यह पॉलिसी उपलब्ध नहीं है।</p>
        <Link to="/" className="pill-btn text-[12px]">{t("go_home")}</Link>
      </div>
    );
  }

  return (
    <SmoothScroll>
      <div className="App font-sans-dev" data-testid={`policy-page-${p.slug}`}>
        <TopMarquee />
        <Header />
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-10 md:py-16 grid grid-cols-12 gap-10">
          {/* Left: current policy content */}
          <article className="col-span-12 lg:col-span-8 min-w-0">
            <Link
              to="/"
              data-testid="policy-back"
              className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#D92525] mb-6"
            >
              <ArrowLeft size={16} /> {t("all_news")}
            </Link>

            <div className="eyebrow text-[#D92525] mb-3">{p.eyebrow}</div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid={`policy-title-${p.slug}`}
              className="font-serif-dev font-black text-[32px] sm:text-[42px] md:text-[52px] leading-[1.05] text-[#0F0F0F]"
            >
              {p.title}
            </motion.h1>
            {p.intro && (
              <p className="mt-5 font-serif-dev italic text-[18px] md:text-[20px] leading-relaxed text-[#4A4A4A]">
                {p.intro}
              </p>
            )}

            <div className="mt-8 space-y-5 font-serif-dev text-[16px] md:text-[17px] leading-[1.85] text-[#1a1a1a]">
              {p.blocks.map((b, i) => {
                if (b.type === "para") {
                  return <p key={`b-${i}`}>{b.text}</p>;
                }
                if (b.type === "list") {
                  return (
                    <ul key={`b-${i}`} className="list-none space-y-2.5 pl-0">
                      {b.items.map((it) => (
                        <li key={it} className="flex gap-3 items-start">
                          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#D92525] shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>

            {p.contact && (
              <div className="mt-10 border-l-4 border-[#D92525] bg-[#F4F4F0] p-5 md:p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#4A4A4A]">
                  {p.contact.label}
                </div>
                <a
                  href={`mailto:${p.contact.email}`}
                  data-testid={`policy-email-${p.slug}`}
                  className="mt-1.5 inline-flex items-center gap-2 font-serif-dev font-black text-[20px] md:text-[24px] text-[#0F0F0F] hover:text-[#D92525] break-all"
                >
                  <Mail size={18} className="shrink-0" />
                  {p.contact.email}
                </a>
              </div>
            )}

            <div className="mt-14 pt-6 border-t border-[#0F0F0F]/10 text-[12px] uppercase tracking-[0.18em] text-[#4A4A4A] flex items-center gap-2">
              <ShieldCheck size={14} className="text-[#D92525]" /> Denva Post · तथ्य, पड़ताल और भरोसे की पत्रकारिता
            </div>
          </article>

          {/* Right: other policies index */}
          <aside className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-24 bg-white border border-[#0F0F0F]/15 p-5 md:p-6">
              <div className="eyebrow text-[#D92525] mb-4">Policies & Info</div>
              <ul className="space-y-2.5 text-[14px] font-serif-dev">
                {POLICY_ORDER.map((k) => {
                  const item = POLICIES[k];
                  const active = k === p.slug;
                  return (
                    <li key={k}>
                      <Link
                        to={item.href}
                        data-testid={`policy-nav-${k}`}
                        className={`block border-l-2 pl-3 py-1 transition-colors ${
                          active
                            ? "border-[#D92525] text-[#D92525] font-bold"
                            : "border-[#0F0F0F]/15 text-[#1a1a1a] hover:border-[#D92525] hover:text-[#D92525]"
                        }`}
                      >
                        {item.eyebrow}
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className="mt-6 pt-4 border-t border-[#0F0F0F]/10">
                <Link to="/#about" data-testid="policy-goto-about" className="text-[12px] uppercase tracking-[0.18em] text-[#4A4A4A] hover:text-[#D92525]">
                  ← About Denva Post
                </Link>
              </div>
            </div>
          </aside>
        </div>
        <Footer />
      </div>
    </SmoothScroll>
  );
}
